import { Link } from 'react-router-dom';
import { USER_ROLE_LABELS } from '@/config/permissions';
import { StatCard, StatCardGrid } from '@/modules/dashboard/components/StatCard';
import { useDashboardStats } from '@/modules/dashboard/hooks/useDashboardStats';
import { buildCalendarPath } from '@/modules/calendar/utils';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

export function DashboardPage() {
  const { profile, hasPermission } = useAuth();
  const { stats, loading, error, hasStats, refresh } = useDashboardStats();
  const showCommercial = hasPermission('commercial.read');
  const showBilling = hasPermission('billing.read');
  const showReservations = hasPermission('reservations.read');
  const showCalendar = hasPermission('calendar.read');
  const showLogistics = hasPermission('logistics.read');
  const showMaintenance = hasPermission('maintenance.read');
  const showMovements = hasPermission('inventory.movements.read');

  return (
    <section className="dashboard-page">
      <header className="dashboard-header">
        <div>
          <h1>Bienvenido{profile ? `, ${profile.displayName}` : ''}</h1>
          {profile ? (
            <p className="auth-muted">
              Rol: <strong>{USER_ROLE_LABELS[profile.role]}</strong> · Empresa{' '}
              {profile.companyId}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="auth-button auth-button--secondary dashboard-refresh"
          onClick={() => void refresh()}
        >
          Actualizar
        </button>
      </header>

      {loading ? <p className="auth-muted">Cargando resumen…</p> : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}

      {hasStats && stats ? (
        <>
          {stats.inventory ? (
            <>
              <h2 className="dashboard-section-title">Inventario</h2>
              <StatCardGrid>
                <StatCard
                  label="Artículos activos"
                  value={stats.inventory.totalItems}
                  hint="Excluye inactivos"
                  accent="blue"
                />
                <StatCard
                  label="Disponibles"
                  value={stats.inventory.available}
                  accent="green"
                />
                <StatCard
                  label="En mantenimiento / reparación"
                  value={stats.inventory.inMaintenance}
                  accent="amber"
                />
                <StatCard
                  label="Reservados (estado operativo)"
                  value={stats.inventory.reserved}
                  accent="purple"
                />
                <StatCard
                  label="Categorías activas"
                  value={stats.inventory.activeCategories}
                />
              </StatCardGrid>
            </>
          ) : null}

          {showCommercial && stats.commercial ? (
            <>
              <h2 className="dashboard-section-title">Comercial</h2>
              <StatCardGrid>
                <StatCard
                  label="Operaciones activas"
                  value={stats.commercial.activeOperations}
                  hint="Pendiente, aprobada o en curso"
                  accent="blue"
                />
                <StatCard
                  label="Presupuestos pendientes"
                  value={stats.commercial.pendingQuotations}
                  accent="amber"
                />
                <StatCard
                  label="Alquileres activos"
                  value={stats.commercial.activeRentals}
                  accent="green"
                />
                <StatCard
                  label="Ventas recientes"
                  value={stats.commercial.recentSales}
                  hint="Últimos 30 días"
                  accent="purple"
                />
                <StatCard
                  label="Clientes activos"
                  value={stats.commercial.activeClients}
                />
              </StatCardGrid>
            </>
          ) : null}

          {showBilling && stats.billing ? (
            <>
              <h2 className="dashboard-section-title">Facturación</h2>
              <StatCardGrid>
                <StatCard
                  label="Facturas emitidas"
                  value={stats.billing.issuedInvoices}
                  accent="green"
                />
                <StatCard
                  label="Facturas pendientes"
                  value={stats.billing.pendingInvoices}
                  hint="Borrador o pendiente"
                  accent="amber"
                />
                <StatCard
                  label="Revenue estimado"
                  value={`$ ${stats.billing.estimatedRevenue.toLocaleString('es-AR')}`}
                  hint="Suma de facturas internas emitidas"
                  accent="blue"
                />
                <StatCard
                  label="Operaciones sin facturar"
                  value={stats.billing.operationsPendingBilling}
                  accent="purple"
                />
              </StatCardGrid>
            </>
          ) : null}

          {(showReservations || showCalendar) &&
          (stats.reservations || stats.calendar) ? (
            <>
              <h2 className="dashboard-section-title">Agenda y reservas</h2>
              <StatCardGrid>
                {showCalendar && stats.calendar ? (
                  <StatCard
                    label="Actividades hoy"
                    value={stats.calendar.todayActivities}
                    accent="blue"
                  />
                ) : null}
                {(stats.calendar || stats.reservations) ? (
                  <StatCard
                    label="Próximas reservas"
                    value={
                      showCalendar && stats.calendar
                        ? stats.calendar.upcomingReservations
                        : (stats.reservations?.upcomingReservations ?? 0)
                    }
                    hint="Inicio en los próximos 14 días"
                    accent="green"
                  />
                ) : null}
                {(stats.calendar || stats.reservations) ? (
                  <StatCard
                    label="Ítems afectados hoy"
                    value={
                      showCalendar && stats.calendar
                        ? stats.calendar.itemsAffectedToday
                        : (stats.reservations?.itemsCurrentlyAffected ?? 0)
                    }
                    accent="amber"
                  />
                ) : null}
                {showCalendar && stats.calendar ? (
                  <StatCard
                    label="Por vencer (7 días)"
                    value={stats.calendar.expiringSoon}
                    accent="purple"
                  />
                ) : null}
                {stats.reservations ? (
                  <StatCard
                    label="Conflictos en datos"
                    value={stats.reservations.detectedConflicts}
                    hint="Solapamientos entre reservas bloqueantes"
                  />
                ) : null}
              </StatCardGrid>
            </>
          ) : null}

          {showMovements && stats.movements ? (
            <>
              <h2 className="dashboard-section-title">Trazabilidad física</h2>
              <StatCardGrid>
                <StatCard
                  label="Ítems fuera"
                  value={stats.movements.itemsOut}
                  hint="Último movimiento = salida"
                  accent="amber"
                />
                <StatCard
                  label="En tránsito"
                  value={stats.movements.itemsInTransit}
                  accent="purple"
                />
                <StatCard
                  label="Transferencias (7 días)"
                  value={stats.movements.recentTransfers}
                  accent="blue"
                />
                <StatCard
                  label="Discrepancias inventario"
                  value={stats.movements.inventoryDiscrepancies}
                  accent="amber"
                />
                <StatCard
                  label="Movimientos recientes"
                  value={stats.movements.recentMovements}
                  hint="Últimos 7 días"
                />
              </StatCardGrid>
              <p className="auth-muted dashboard-kpi-note">
                KPIs futuros: utilization rate, transfer frequency, downtime,
                inventory accuracy — ver docs/INVENTORY_MOVEMENTS.md.
              </p>
            </>
          ) : null}

          {showMaintenance && stats.maintenance ? (
            <>
              <h2 className="dashboard-section-title">Mantenimiento</h2>
              <StatCardGrid>
                <StatCard
                  label="Ítems en mantenimiento"
                  value={stats.maintenance.itemsInMaintenance}
                  accent="amber"
                />
                <StatCard
                  label="Reparaciones activas"
                  value={stats.maintenance.activeRepairs}
                  accent="purple"
                />
                <StatCard
                  label="Inspecciones pendientes"
                  value={stats.maintenance.pendingInspections}
                  accent="blue"
                />
                <StatCard
                  label="Conflictos mant./reserva"
                  value={stats.maintenance.maintenanceReservationConflicts}
                  hint="Tareas activas que solapan reservas"
                />
              </StatCardGrid>
              <p className="auth-muted dashboard-kpi-note">
                KPIs futuros (sin analytics avanzado): frecuencia de mantenimiento,
                reparaciones, downtime y impacto en utilización — ver{' '}
                <code>docs/MAINTENANCE_ARCHITECTURE.md</code>.
              </p>
            </>
          ) : null}

          {showLogistics && stats.logistics ? (
            <>
              <h2 className="dashboard-section-title">Logística</h2>
              <StatCardGrid>
                <StatCard
                  label="Entregas pendientes"
                  value={stats.logistics.pendingDeliveries}
                  accent="blue"
                />
                <StatCard
                  label="Retiros pendientes"
                  value={stats.logistics.pendingPickups}
                  accent="green"
                />
                <StatCard
                  label="En tránsito"
                  value={stats.logistics.inTransit}
                  accent="amber"
                />
                <StatCard
                  label="Próximas operaciones"
                  value={stats.logistics.upcomingOperations}
                  hint="14 días"
                  accent="purple"
                />
              </StatCardGrid>
            </>
          ) : null}

          {showCalendar &&
          stats.calendar &&
          stats.calendar.highOccupancyItems.length > 0 ? (
            <div className="dashboard-occupancy">
              <h3 className="dashboard-section-title">Alta ocupación (30 días)</h3>
              <ul className="dashboard-occupancy-list">
                {stats.calendar.highOccupancyItems.map((row) => (
                  <li key={row.itemId}>
                    <Link to={buildCalendarPath({ itemId: row.itemId })}>
                      {row.label}
                    </Link>
                    <span className="auth-muted"> — {row.count} reservas</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="dashboard-quick">
        <h2>Accesos rápidos</h2>
        <ul className="inventory-cards">
          <li>
            <Link to={modulePaths.inventory} className="inventory-card">
              <strong>Inventario</strong>
              <span>Ver y gestionar artículos</span>
            </Link>
          </li>
          {showCommercial ? (
            <li>
              <Link
                to={`${modulePaths.commercial}/operaciones`}
                className="inventory-card"
              >
                <strong>Comercial</strong>
                <span>Clientes y operaciones</span>
              </Link>
            </li>
          ) : null}
          {showBilling ? (
            <li>
              <Link
                to={`${modulePaths.billing}/facturas`}
                className="inventory-card"
              >
                <strong>Facturación</strong>
                <span>Facturas internas</span>
              </Link>
            </li>
          ) : null}
          {showReservations ? (
            <li>
              <Link to={modulePaths.reservations} className="inventory-card">
                <strong>Reservas</strong>
                <span>Alquiler y disponibilidad</span>
              </Link>
            </li>
          ) : null}
          {showCalendar ? (
            <li>
              <Link to={modulePaths.calendar} className="inventory-card">
                <strong>Agenda</strong>
                <span>Calendario operativo</span>
              </Link>
            </li>
          ) : null}
          {showLogistics ? (
            <li>
              <Link to={modulePaths.logistics} className="inventory-card">
                <strong>Logística</strong>
                <span>Entregas, retiros y proveedores</span>
              </Link>
            </li>
          ) : null}
          <li>
            <Link to={modulePaths.categories} className="inventory-card">
              <strong>Categorías</strong>
              <span>Organizar el catálogo</span>
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
}
